import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import NavigationBreadcrumb from '../../components/navigation/NavigationBreadcrumb';
import NotificationIndicator from '../../components/navigation/NotificationIndicator';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import ClientTableRow from './components/ClientTableRow';
import ClientCard from './components/ClientCard';
import ClientFilters from './components/ClientFilters';
import ClientFormModal from './components/ClientFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import BulkActionsBar from './components/BulkActionsBar';

const ClientManagement = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'fr');
  const [direction, setDirection] = useState(() => localStorage.getItem('direction') || 'ltr');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedClients, setSelectedClients] = useState([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [deletingClient, setDeletingClient] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    practiceArea: 'all',
    dateFrom: '',
    dateTo: ''
  });

  const mockClients = [
    {
      id: 1,
      clientId: "CLT-2024-001",
      name: "Mohammed Alami",
      email: "m.alami@email.ma",
      phone: "+212 661 234 567",
      address: "45 Boulevard Mohammed V",
      city: "Casablanca",
      postalCode: "20000",
      status: "active",
      activeCases: 3,
      practiceArea: { en: "Commercial Law", fr: "Droit commercial", ar: "القانون التجاري" },
      practiceAreaValue: "commercial",
      lastInteraction: "2026-01-25T10:30:00",
      notes: "Client important avec plusieurs dossiers en cours"
    },
    {
      id: 2,
      clientId: "CLT-2024-002",
      name: "Fatima Benali",
      email: "f.benali@email.ma",
      phone: "+212 662 345 678",
      address: "12 Rue Hassan II",
      city: "Rabat",
      postalCode: "10000",
      status: "active",
      activeCases: 1,
      practiceArea: { en: "Family Law", fr: "Droit de la famille", ar: "قانون الأسرة" },
      practiceAreaValue: "family",
      lastInteraction: "2026-01-26T14:15:00",
      notes: "Dossier de divorce en cours"
    },
    {
      id: 3,
      clientId: "CLT-2024-003",
      name: "Youssef Tazi",
      email: "y.tazi@email.ma",
      phone: "+212 663 456 789",
      address: "78 Avenue des FAR",
      city: "Marrakech",
      postalCode: "40000",
      status: "pending",
      activeCases: 0,
      practiceArea: { en: "Civil Law", fr: "Droit civil", ar: "القانون المدني" },
      practiceAreaValue: "civil",
      lastInteraction: "2026-01-20T09:00:00",
      notes: "Nouveau client - consultation initiale effectuée"
    },
    {
      id: 4,
      clientId: "CLT-2024-004",
      name: "Amina Chakir",
      email: "a.chakir@email.ma",
      phone: "+212 664 567 890",
      address: "23 Rue de la Liberté",
      city: "Fès",
      postalCode: "30000",
      status: "active",
      activeCases: 2,
      practiceArea: { en: "Labor Law", fr: "Droit du travail", ar: "قانون العمل" },
      practiceAreaValue: "labor",
      lastInteraction: "2026-01-27T11:45:00",
      notes: "Litige avec employeur en cours"
    },
    {
      id: 5,
      clientId: "CLT-2024-005",
      name: "Karim Idrissi",
      email: "k.idrissi@email.ma",
      phone: "+212 665 678 901",
      address: "56 Boulevard Zerktouni",
      city: "Casablanca",
      postalCode: "20100",
      status: "inactive",
      activeCases: 0,
      practiceArea: { en: "Criminal Law", fr: "Droit pénal", ar: "القانون الجنائي" },
      practiceAreaValue: "criminal",
      lastInteraction: "2025-12-15T16:30:00",
      notes: "Dossier clôturé - client satisfait"
    },
    {
      id: 6,
      clientId: "CLT-2024-006",
      name: "Salma Benjelloun",
      email: "s.benjelloun@email.ma",
      phone: "+212 666 789 012",
      address: "89 Avenue Mohammed VI",
      city: "Tanger",
      postalCode: "90000",
      status: "active",
      activeCases: 4,
      practiceArea: { en: "Commercial Law", fr: "Droit commercial", ar: "القانون التجاري" },
      practiceAreaValue: "commercial",
      lastInteraction: "2026-01-27T15:20:00",
      notes: "Entreprise avec plusieurs contrats en révision"
    },
    {
      id: 7,
      clientId: "CLT-2024-007",
      name: "Hassan Lahlou",
      email: "h.lahlou@email.ma",
      phone: "+212 667 890 123",
      address: "34 Rue Allal Ben Abdellah",
      city: "Rabat",
      postalCode: "10100",
      status: "active",
      activeCases: 1,
      practiceArea: { en: "Civil Law", fr: "Droit civil", ar: "القانون المدني" },
      practiceAreaValue: "civil",
      lastInteraction: "2026-01-24T10:00:00",
      notes: "Litige immobilier"
    },
    {
      id: 8,
      clientId: "CLT-2024-008",
      name: "Nadia Berrada",
      email: "n.berrada@email.ma",
      phone: "+212 668 901 234",
      address: "67 Boulevard Anfa",
      city: "Casablanca",
      postalCode: "20250",
      status: "pending",
      activeCases: 0,
      practiceArea: { en: "Family Law", fr: "Droit de la famille", ar: "قانون الأسرة" },
      practiceAreaValue: "family",
      lastInteraction: "2026-01-22T13:30:00",
      notes: "Consultation pour garde d\'enfants"
    }
  ];

  const [clients, setClients] = useState(mockClients);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'fr';
    const savedDirection = localStorage.getItem('direction') || 'ltr';
    setLanguage(savedLanguage);
    setDirection(savedDirection);
    document.documentElement?.setAttribute('dir', savedDirection);
  }, []);

  const pageLabels = {
    title: { en: 'Client Management', fr: 'Gestion des clients', ar: 'إدارة العملاء' },
    subtitle: { en: 'Manage client profiles and information', fr: 'Gérer les profils et informations clients', ar: 'إدارة ملفات تعريف العملاء والمعلومات' },
    addClient: { en: 'Add Client', fr: 'Ajouter un client', ar: 'إضافة عميل' },
    search: { en: 'Search clients...', fr: 'Rechercher des clients...', ar: 'البحث عن العملاء...' },
    exportAll: { en: 'Export All', fr: 'Tout exporter', ar: 'تصدير الكل' },
    name: { en: 'Name', fr: 'Nom', ar: 'الاسم' },
    contact: { en: 'Contact', fr: 'Contact', ar: 'جهة الاتصال' },
    status: { en: 'Status', fr: 'Statut', ar: 'الحالة' },
    activeCases: { en: 'Active Cases', fr: 'Dossiers actifs', ar: 'القضايا النشطة' },
    practiceArea: { en: 'Practice Area', fr: 'Domaine', ar: 'المجال' },
    lastInteraction: { en: 'Last Interaction', fr: 'Dernière interaction', ar: 'آخر تفاعل' },
    actions: { en: 'Actions', fr: 'Actions', ar: 'الإجراءات' },
    noResults: { en: 'No clients found', fr: 'Aucun client trouvé', ar: 'لم يتم العثور على عملاء' },
    selectAll: { en: 'Select all', fr: 'Tout sélectionner', ar: 'تحديد الكل' }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleResetFilters = () => {
    setFilters({
      status: 'all',
      practiceArea: 'all',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setIsFormModalOpen(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setIsFormModalOpen(true);
  };

  const handleDeleteClient = (client) => {
    setDeletingClient(client);
    setIsDeleteModalOpen(true);
  };

  const handleSaveClient = (formData) => {
    if (editingClient) {
      setClients(clients?.map(c => c?.id === editingClient?.id ? { ...c, ...formData } : c));
    } else {
      const newClient = {
        id: clients?.length + 1,
        clientId: `CLT-2026-${String(clients?.length + 1)?.padStart(3, '0')}`,
        ...formData,
        activeCases: 0,
        lastInteraction: new Date()?.toISOString(),
        practiceArea: {
          en: formData?.practiceArea === 'civil' ? 'Civil Law' : formData?.practiceArea === 'criminal' ? 'Criminal Law' : formData?.practiceArea === 'commercial' ? 'Commercial Law' : formData?.practiceArea === 'family' ? 'Family Law' : 'Labor Law',
          fr: formData?.practiceArea === 'civil' ? 'Droit civil' : formData?.practiceArea === 'criminal' ? 'Droit pénal' : formData?.practiceArea === 'commercial' ? 'Droit commercial' : formData?.practiceArea === 'family' ? 'Droit de la famille' : 'Droit du travail',
          ar: formData?.practiceArea === 'civil' ? 'القانون المدني' : formData?.practiceArea === 'criminal' ? 'القانون الجنائي' : formData?.practiceArea === 'commercial' ? 'القانون التجاري' : formData?.practiceArea === 'family' ? 'قانون الأسرة' : 'قانون العمل'
        },
        practiceAreaValue: formData?.practiceArea
      };
      setClients([...clients, newClient]);
    }
  };

  const handleConfirmDelete = (client) => {
    setClients(clients?.filter(c => c?.id !== client?.id));
  };

  const handleViewCases = (client) => {
    navigate('/case-management', { state: { clientId: client?.id } });
  };

  const handleGenerateInvoice = (client) => {
    navigate('/billing-invoice', { state: { clientId: client?.id } });
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev =>
      prev?.includes(clientId)
        ? prev?.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleExportSelected = () => {
    console.log('Exporting selected clients:', selectedClients);
  };

  const handleExportAll = () => {
    console.log('Exporting all clients');
  };

  const filteredClients = clients?.filter(client => {
    const matchesSearch = client?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         client?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         client?.clientId?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesStatus = filters?.status === 'all' || client?.status === filters?.status;
    const matchesPracticeArea = filters?.practiceArea === 'all' || client?.practiceAreaValue === filters?.practiceArea;
    
    let matchesDateRange = true;
    if (filters?.dateFrom || filters?.dateTo) {
      const interactionDate = new Date(client.lastInteraction);
      if (filters?.dateFrom) {
        matchesDateRange = matchesDateRange && interactionDate >= new Date(filters.dateFrom);
      }
      if (filters?.dateTo) {
        matchesDateRange = matchesDateRange && interactionDate <= new Date(filters.dateTo);
      }
    }

    return matchesSearch && matchesStatus && matchesPracticeArea && matchesDateRange;
  });

  const sortedClients = [...filteredClients]?.sort((a, b) => {
    let aValue = a?.[sortColumn];
    let bValue = b?.[sortColumn];

    if (sortColumn === 'practiceArea') {
      aValue = a?.practiceArea?.[language];
      bValue = b?.practiceArea?.[language];
    }

    if (typeof aValue === 'string') {
      return sortDirection === 'asc'
        ? aValue?.localeCompare(bValue)
        : bValue?.localeCompare(aValue);
    }

    return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
  });

  const handleSelectAll = () => {
    if (selectedClients?.length === filteredClients?.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients?.map(c => c?.id));
    }
  };

  return (
    <div className="min-h-screen bg-background" style={{ direction }}>
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div
        className={`transition-all duration-250 pt-16 lg:pt-0 ${
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-sm px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <NavigationBreadcrumb language={language} />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mt-2">
                {pageLabels?.title?.[language]}
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">
                {pageLabels?.subtitle?.[language]}
              </p>
            </div>
            <NotificationIndicator language={language} />
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <ClientFilters
            language={language}
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />

          <div className="bg-card border border-border rounded-lg shadow-elevation-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-4 md:px-6 py-4 border-b border-border">
              <div className="flex-1 max-w-md">
                <Input
                  type="search"
                  placeholder={pageLabels?.search?.[language]}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="w-full"
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportAll}
                  iconName="Download"
                  iconPosition="left"
                  iconSize={14}
                >
                  {pageLabels?.exportAll?.[language]}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAddClient}
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={14}
                >
                  {pageLabels?.addClient?.[language]}
                </Button>
              </div>
            </div>

            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <Checkbox
                        checked={selectedClients?.length === sortedClients?.length && sortedClients?.length > 0}
                        onChange={handleSelectAll}
                        label={pageLabels?.selectAll?.[language]}
                      />
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-2">
                        {pageLabels?.name?.[language]}
                        <Icon
                          name={sortColumn === 'name' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                          size={16}
                          className={sortColumn === 'name' ? 'text-primary' : 'text-muted-foreground'}
                        />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {pageLabels?.contact?.[language]}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-2">
                        {pageLabels?.status?.[language]}
                        <Icon
                          name={sortColumn === 'status' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                          size={16}
                          className={sortColumn === 'status' ? 'text-primary' : 'text-muted-foreground'}
                        />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleSort('activeCases')}
                    >
                      <div className="flex items-center gap-2">
                        {pageLabels?.activeCases?.[language]}
                        <Icon
                          name={sortColumn === 'activeCases' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                          size={16}
                          className={sortColumn === 'activeCases' ? 'text-primary' : 'text-muted-foreground'}
                        />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {pageLabels?.practiceArea?.[language]}
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-foreground cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleSort('lastInteraction')}
                    >
                      <div className="flex items-center gap-2">
                        {pageLabels?.lastInteraction?.[language]}
                        <Icon
                          name={sortColumn === 'lastInteraction' && sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                          size={16}
                          className={sortColumn === 'lastInteraction' ? 'text-primary' : 'text-muted-foreground'}
                        />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">
                      {pageLabels?.actions?.[language]}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedClients?.map((client) => (
                    <ClientTableRow
                      key={client?.id}
                      client={client}
                      language={language}
                      isSelected={selectedClients?.includes(client?.id)}
                      onSelect={() => handleSelectClient(client?.id)}
                      onEdit={handleEditClient}
                      onViewCases={handleViewCases}
                      onGenerateInvoice={handleGenerateInvoice}
                      onDelete={handleDeleteClient}
                    />
                  ))}
                </tbody>
              </table>

              {sortedClients?.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{pageLabels?.noResults?.[language]}</p>
                </div>
              )}
            </div>

            <div className="lg:hidden p-4 space-y-4">
              {sortedClients?.map((client) => (
                <ClientCard
                  key={client?.id}
                  client={client}
                  language={language}
                  onEdit={handleEditClient}
                  onViewCases={handleViewCases}
                  onGenerateInvoice={handleGenerateInvoice}
                  onDelete={handleDeleteClient}
                />
              ))}

              {sortedClients?.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{pageLabels?.noResults?.[language]}</p>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
      <ClientFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        client={editingClient}
        language={language}
        onSave={handleSaveClient}
      />
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        client={deletingClient}
        language={language}
        onConfirm={handleConfirmDelete}
      />
      <BulkActionsBar
        selectedCount={selectedClients?.length}
        language={language}
        onExport={handleExportSelected}
        onClearSelection={() => setSelectedClients([])}
      />
    </div>
  );
};

export default ClientManagement;